import { Routes } from '@angular/router';
import { ExitConfirmationGuard } from 'src/app/core/guards/exit-confirmation.guard';
import { AuthGuard } from 'src/app/core/guards/auth.guard';
import { TestComponentsComponent } from 'src/app/features/test-components/test-components.component';
import { ViewerRouteGuard } from 'src/app/core/guards/viewer-route.guard';
import { NonUserRouteGuard } from 'src/app/core/guards/non-user-route.guard';
import { SuperuserRouteGuard } from 'src/app/core/guards/superuser-route/superuser-route.guard';
import { ScheduledRoundCreateGuard } from 'src/app/core/guards/scheduled-round-create.guard';
import { ScheduledRoundEditGuard } from 'src/app/core/guards/scheduled-round-edit.guard';
import { ScheduledRoundsAvailabilityGuard } from 'src/app/core/guards/scheduled-rounds-availability.guard';
import { EmployeeStatisticProfileGuard } from 'src/app/core/guards/employee-statistic-profile.guard';
import { FIRST_TIME_HERE_FORM_TEXTS } from 'src/app/features/auth/constants/password-link-form-texts.const';

export const routes: Routes = [
  // { path: 'test', component: TestComponentsComponent, data: { title: 'Test Components' } },
  {
    path: 'auth',
    loadComponent: () => import('./features/auth/auth.component').then(m => m.AuthComponent),
    children: [
      { path: '', redirectTo: 'sign-in', pathMatch: 'full' },
      {
        path: 'google',
        loadComponent: () => import('./features/auth/pages/google/google.component').then(m => m.GoogleComponent),
      },
      {
        path: 'outlook',
        loadComponent: () => import('./features/auth/pages/outlook/outlook.component').then(m => m.OutlookComponent),
      },
      { path: 'sign-up', redirectTo: 'first-time', pathMatch: 'full' },
      {
        path: 'sign-in',
        loadComponent: () => import('./features/auth/pages/sign-in/sign-in.component').then(m => m.SignInComponent),
      },
      {
        path: 'forgot-password',
        loadComponent: () =>
          import('./features/auth/pages/forgot-password/forgot-password.component').then(
            m => m.ForgotPasswordComponent
          ),
      },
      {
        path: 'first-time',
        loadComponent: () =>
          import('./features/auth/pages/forgot-password/forgot-password.component').then(
            m => m.ForgotPasswordComponent
          ),
        data: { texts: FIRST_TIME_HERE_FORM_TEXTS },
      },
      {
        path: 'reset-password',
        loadComponent: () =>
          import('./features/auth/pages/reset-password/reset-password.component').then(m => m.ResetPasswordComponent),
      },
    ],
  },
  {
    path: '',
    loadComponent: () => import('./core/layout/layout.component').then(m => m.LayoutComponent),
    canActivate: [AuthGuard],
    canActivateChild: [ViewerRouteGuard, NonUserRouteGuard],
    children: [
      { path: '', redirectTo: 'homepage', pathMatch: 'full' },
      {
        path: 'notifications',
        loadComponent: () =>
          import('./features/notification/pages/notification-list/notification-list.component').then(
            m => m.NotificationListComponent
          ),
        data: { title: 'Notifications' },
      },
      {
        path: 'report',
        loadComponent: () => import('./features/report/report.component').then(m => m.ReportComponent),
      },
      {
        path: 'homepage',
        loadComponent: () => import('./features/home-page/home-page.component').then(m => m.HomePageComponent),
      },
      {
        path: 'scheduled-rounds/new',
        canActivate: [ScheduledRoundsAvailabilityGuard, ScheduledRoundCreateGuard],
        canDeactivate: [ExitConfirmationGuard],
        loadComponent: () =>
          import('./features/scheduled-rounds/scheduled-round-form/scheduled-round-form.component').then(
            m => m.ScheduledRoundFormComponent
          ),
        data: { title: 'Schedule Round' },
      },
      {
        path: 'scheduled-rounds/edit/:id',
        canActivate: [ScheduledRoundsAvailabilityGuard, ScheduledRoundEditGuard],
        canDeactivate: [ExitConfirmationGuard],
        loadComponent: () =>
          import('./features/scheduled-rounds/scheduled-round-form/scheduled-round-form.component').then(
            m => m.ScheduledRoundFormComponent
          ),
        data: { title: 'Edit Scheduled Round' },
      },
      {
        path: 'scheduled-rounds',
        canActivate: [ScheduledRoundsAvailabilityGuard],
        loadComponent: () =>
          import('./features/scheduled-rounds/scheduled-rounds.component').then(m => m.ScheduledRoundsComponent),
        data: { title: 'My Scheduled Rounds' },
      },
      {
        path: 'settings',
        loadComponent: () =>
          import('./features/profile-settings/profile-settings.component').then(m => m.ProfileSettingsComponent),
        data: { title: 'Profile Settings' },
        children: [
          { path: '', redirectTo: 'homepage', pathMatch: 'full' },
          {
            path: 'homepage',
            loadComponent: () =>
              import('./features/profile-settings/pages/profile-settings-home-page/profile-settings-home-page.component').then(
                m => m.ProfileSettingsHomePageComponent
              ),
          },
          {
            path: 'security',
            loadComponent: () =>
              import('./features/profile-settings/pages/profile-settings-security/profile-settings-security.component').then(
                m => m.ProfileSettingsSecurityComponent
              ),
            canDeactivate: [ExitConfirmationGuard],
          },
        ],
      },
      {
        path: 'system-settings',
        loadComponent: () =>
          import('./features/profile-settings/profile-settings.component').then(m => m.ProfileSettingsComponent),
        data: { title: 'System Settings' },
        children: [
          {
            path: '',
            loadComponent: () =>
              import('src/app/features/system-setting/system-setting.component').then(m => m.SystemSettingComponent),
            canDeactivate: [ExitConfirmationGuard],
          },
        ],
      },
      {
        path: 'templates',
        loadComponent: () =>
          import('./features/rounding-templates/template-page.component').then(m => m.TemplatePageComponent),
        data: { title: 'Rounding Templates' },
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./features/rounding-templates/rounding-templates/rounding-templates.component').then(
                m => m.RoundingTemplatesComponent
              ),
          },
          {
            path: 'new-template',
            loadComponent: () =>
              import('./features/rounding-templates/new-rounding-template/new-rounding-template.component').then(
                m => m.NewRoundingTemplateComponent
              ),
            canDeactivate: [ExitConfirmationGuard],
          },
          {
            path: 'edit-template/:id',
            loadComponent: () =>
              import('./features/rounding-templates/new-rounding-template/new-rounding-template.component').then(
                m => m.NewRoundingTemplateComponent
              ),
            canDeactivate: [ExitConfirmationGuard],
          },
          {
            path: 'view-template/:id',
            loadComponent: () =>
              import('./features/rounding-templates/view-rounding-template/view-rounding-template.component').then(
                m => m.ViewRoundingTemplateComponent
              ),
          },
          {
            path: 'manage-templates',
            loadComponent: () =>
              import('./features/rounding-templates/manage-rounding-templates/manage-rounding-templates.component').then(
                m => m.ManageRoundingTemplatesComponent
              ),
          },
        ],
      },
      {
        path: 'new-round',
        loadComponent: () => import('./features/new-round/new-round-page.component').then(m => m.NewRoundPageComponent),
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./features/new-round/components/new-round-roundee/new-round-roundee.component').then(
                m => m.NewRoundRoundeeComponent
              ),
          },
          {
            path: 'template',
            loadComponent: () =>
              import('./features/new-round/components/new-round-template/new-round-template.component').then(
                m => m.NewRoundTemplateComponent
              ),
          },
          {
            path: 'library',
            loadComponent: () =>
              import('./features/new-round/components/new-round-template-library/new-round-template-library.component').then(
                m => m.NewRoundTemplateLibraryComponent
              ),
          },
          {
            path: 'start',
            loadComponent: () =>
              import('./features/new-round/components/round/round.component').then(m => m.RoundComponent),
            canDeactivate: [ExitConfirmationGuard],
          },
        ],
      },
      {
        path: 'past-rounds',
        loadComponent: () => import('./features/past-rounds/round-page.component').then(m => m.RoundPageComponent),
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./features/past-rounds/components/past-rounds/past-rounds.component').then(
                m => m.PastRoundsComponent
              ),
          },
          {
            path: 'roundee',
            loadComponent: () =>
              import('./features/past-rounds/components/past-round-roundee/past-round-roundee.component').then(
                m => m.PastRoundRoundeeComponent
              ),
          },
          {
            path: 'template',
            loadComponent: () =>
              import('./features/past-rounds/components/past-round-template-library/past-round-template-library.component').then(
                m => m.PastRoundTemplateLibraryComponent
              ),
          },
          {
            path: 'list',
            loadComponent: () =>
              import('./features/past-rounds/components/past-rounds-list/past-rounds-list.component').then(
                m => m.PastRoundsListComponent
              ),
          },
          {
            path: 'details/:id',
            loadComponent: () =>
              import('./features/past-rounds/components/past-round-details/past-round-details.component').then(
                m => m.PastRoundDetailsComponent
              ),
            canDeactivate: [ExitConfirmationGuard],
          },
        ],
      },
      {
        path: 'recognition',
        loadComponent: () => import('./features/recognition/recognition.component').then(m => m.RecognitionComponent),
        data: { title: 'Recognition' },
        children: [
          {
            path: 'list',
            loadComponent: () =>
              import('./features/recognition/pages/recognition-list/recognition-list.component').then(
                m => m.RecognitionListComponent
              ),
          },
          {
            path: ':id',
            loadComponent: () =>
              import('./features/recognition/pages/recognition-details/recognition-details.component').then(
                m => m.RecognitionDetailsComponent
              ),
          },
        ],
      },
      {
        path: 'issue-view',
        loadComponent: () =>
          import('src/app/features/issue/pages/view-issues/view-issues.component').then(m => m.ViewIssuesComponent),
        data: { title: 'View Issues' },
        children: [
          { path: '', redirectTo: 'list', pathMatch: 'full' },
          {
            path: 'list',
            loadComponent: () =>
              import('./features/issue/components/issue-list/issue-list.component').then(m => m.IssueListComponent),
            children: [],
          },
          {
            path: 'details/:id',
            loadComponent: () =>
              import('./features/issue/pages/issue-details/issue-details.component').then(m => m.IssueDetailsComponent),
          },
        ],
      },
      {
        path: 'management',
        loadComponent: () => import('./features/management/management.component').then(m => m.ManagementComponent),
        data: { title: 'Management' },
        children: [
          {
            path: 'user',
            loadComponent: () =>
              import('src/app/features/management/pages/user-management/user-management.component').then(
                m => m.UserManagementComponent
              ),
          },
        ],
      },
      {
        path: 'internal-management',
        canMatch: [SuperuserRouteGuard],
        canActivate: [SuperuserRouteGuard],
        loadComponent: () =>
          import('./features/management/pages/internal-management/internal-management.component').then(
            m => m.InternalManagementComponent
          ),
        data: { title: 'Internal Management' },
      },
      {
        path: 'employee-statistics',
        data: { title: 'Employee Statistics' },
        loadComponent: () =>
          import('./features/employee-statistic/employee-statistic.component').then(m => m.EmployeeStatisticComponent),
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./features/employee-statistic/pages/employee-statistic-overall/employee-statistic-overall.component').then(
                m => m.EmployeeStatisticOverallComponent
              ),
          },
          {
            path: 'table',
            loadComponent: () =>
              import('src/app/features/employee-statistic/pages/employee-statistic-tables/employee-statistic-tables.component').then(
                m => m.EmployeeStatisticTablesComponent
              ),
          },
          {
            path: 'profile',
            canActivate: [EmployeeStatisticProfileGuard],
            loadComponent: () =>
              import('./features/employee-statistic/pages/employee-statistic-profile/employee-statistic-profile.component').then(
                m => m.EmployeeStatisticProfileComponent
              ),
          },
          {
            path: 'round-details/:id',
            loadComponent: () =>
              import('./features/past-rounds/components/past-round-details/past-round-details.component').then(
                m => m.PastRoundDetailsComponent
              ),
            canDeactivate: [ExitConfirmationGuard],
          },
          {
            path: 'recognition-details/:id',
            loadComponent: () =>
              import('./features/recognition/pages/recognition-details/recognition-details.component').then(
                m => m.RecognitionDetailsComponent
              ),
          },
          {
            path: 'issue-details/:id',
            loadComponent: () =>
              import('./features/issue/pages/issue-details/issue-details.component').then(m => m.IssueDetailsComponent),
            data: { allowEdit: false },
          },
        ],
      },
      {
        path: 'dashboard/reports/overall',
        loadComponent: () =>
          import('./features/dashboard/pages/reports-overall/reports-overall.component').then(m => m.ReportsOverall),
      },
      {
        path: 'dashboard',
        data: { title: 'Dashboards & Reports' },
        loadComponent: () => import('./features/dashboard/dashboard.component').then(m => m.DashboardComponent),
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./features/dashboard/pages/dashboard-statistic/dashboard-statistic.component').then(
                m => m.DashboardStatisticComponent
              ),
          },

          {
            path: 'rounds',
            loadComponent: () =>
              import('./features/dashboard/pages/rounds-statistic/rounds-statistic.component').then(
                m => m.RoundsStatisticComponent
              ),
            children: [
              {
                path: 'overall',
                loadComponent: () =>
                  import('./features/dashboard/pages/rounds-statistic/components/rounds-statistic-overall/rounds-statistic-overall.component').then(
                    m => m.RoundsStatisticOverallComponent
                  ),
              },
              {
                path: 'details/:id',
                loadComponent: () =>
                  import('./features/dashboard/pages/rounds-statistic/components/rounds-statistic-details/rounds-statistic-details.component').then(
                    m => m.RoundsStatisticDetailsComponent
                  ),
              },
              {
                path: 'round/:id',
                loadComponent: () =>
                  import('./features/past-rounds/components/past-round-details/past-round-details.component').then(
                    m => m.PastRoundDetailsComponent
                  ),
              },
            ],
          },
          {
            path: 'issues',
            loadComponent: () =>
              import('./features/dashboard/pages/issues-statistic/issues-statistic.component').then(
                m => m.IssuesStatisticComponent
              ),
            children: [
              {
                path: 'overall',
                loadComponent: () =>
                  import('./features/dashboard/pages/issues-statistic/components/issues-statistic-overall/issues-statistic-overall.component').then(
                    m => m.IssuesStatisticOverallComponent
                  ),
              },
              {
                path: 'details/:status',
                loadComponent: () =>
                  import('./features/dashboard/pages/issues-statistic/components/issues-statistic-details/issues-statistic-details.component').then(
                    m => m.IssuesStatisticDetailsComponent
                  ),
              },
            ],
          },
          {
            path: 'issue-details/:id',
            loadComponent: () =>
              import('./features/issue/pages/issue-details/issue-details.component').then(m => m.IssueDetailsComponent),
            data: { allowEdit: false },
          },
          {
            path: 'recognitions',
            loadComponent: () =>
              import('./features/dashboard/pages/recognitions-statistic/recognitions-statistic.component').then(
                m => m.RecognitionsStatisticComponent
              ),
            children: [
              {
                path: 'overall',
                loadComponent: () =>
                  import('./features/dashboard/pages/recognitions-statistic/components/recognition-statistic-overall/recognition-statistic-overall.component').then(
                    m => m.RecognitionStatisticOverallComponent
                  ),
              },
              {
                path: 'details/:id',
                loadComponent: () =>
                  import('./features/recognition/pages/recognition-details/recognition-details.component').then(
                    m => m.RecognitionDetailsComponent
                  ),
              },
            ],
          },
          {
            path: 'ai-summary/:type/:coverage',
            loadComponent: () =>
              import('./features/dashboard/pages/dashboard-ai-summary/dashboard-ai-summary.component').then(
                m => m.DashboardAiSummaryComponent
              ),
          },
          {
            path: 'reports',
            loadComponent: () =>
              import('./features/dashboard/pages/reports-statistic/reports-statistic.component').then(
                m => m.ReportsStatisticComponent
              ),
            children: [
              {
                path: 'details/:id',
                loadComponent: () =>
                  import('./features/dashboard/pages/dashboard-ai-summary-V2/dashboard-ai-summary-v2.component').then(
                    m => m.DashboardAiSummaryV2Component
                  ),
              },
            ],
          },
        ],
      },
      {
        path: 'not-found',
        loadComponent: () =>
          import('./core/layout/not-found-page/not-found-page.component').then(m => m.NotFoundPageComponent),
      },
    ],
  },
  { path: '**', redirectTo: '' },
];
